import React, { useState, useEffect } from 'react';
import { Property } from '../types/property.types';
import { propertyService } from '../services/propertyService';

interface PlatformUrlsProps {
  property: Property;
  showTitle?: boolean;
  compact?: boolean;
}

interface PlatformUrlResult {
  success: boolean;
  code: string;
  platform_urls: Record<string, string>;
  summary: {
    total_platforms: number;
    found_count: number;
    success_rate: string;
    best_platform?: string;
    total_time_ms: number;
  };
  timestamp: string;
}

const PLATFORM_NAMES: Record<string, { name: string; color: string; icon: string }> = {
  'prointegral': { name: 'ProIntegral', color: 'bg-blue-500', icon: '🏢' },
  'fincarraiz': { name: 'FincaRaíz', color: 'bg-green-500', icon: '🏠' },
  'metrocuadrado': { name: 'MetroCuadrado', color: 'bg-purple-500', icon: '📏' },
  'ciencuadras': { name: 'CienCuadras', color: 'bg-orange-500', icon: '💯' },
  'proppit': { name: 'PropPit', color: 'bg-red-500', icon: '🎯' }
};

export const PlatformUrls: React.FC<PlatformUrlsProps> = ({ 
  property, 
  showTitle = true, 
  compact = false 
}) => {
  const [urls, setUrls] = useState<PlatformUrlResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(!compact);

  const searchPlatformUrls = async () => {
    if (!property.codigo_inmueble && !property.id) {
      setError('La propiedad necesita un código para buscar URLs');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const searchCode = property.codigo_inmueble || property.id;
      console.log('🔍 Buscando URLs para código:', searchCode);

      // Intentar primero con el código directo
      let result = await propertyService.getPlatformUrls(searchCode);
      
      // Si no encuentra resultados, intentar búsqueda avanzada
      if (!result.success || result.summary.found_count === 0) {
        console.log('🔄 Intentando búsqueda avanzada...');
        result = await propertyService.searchPlatformUrls({
          codigo: searchCode,
          tipo_inmueble: property.tipo_inmueble,
          transaccion: property.transaccion,
          ciudad: property.ciudad,
          sector: property.sector,
          valor_venta: property.valor_venta,
          valor_arriendo: property.valor_arriendo
        });
      }

      setUrls(result);
      console.log('✅ URLs encontradas:', result);
    } catch (err) {
      console.error('❌ Error buscando URLs:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (property && !compact) {
      // Buscar automáticamente al cargar
      searchPlatformUrls();
    }
  }, [property.id, property.codigo_inmueble]);

  if (compact && !expanded) {
    return (
      <div className="flex items-center space-x-2">
        <button
          onClick={() => setExpanded(true)}
          className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
        >
          <span>🔗</span>
          <span>Ver enlaces</span>
        </button>
        {urls && urls.summary.found_count > 0 && (
          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
            {urls.summary.found_count} encontrados
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={`${compact ? 'border-t pt-3 mt-3' : 'bg-white p-4 rounded-lg shadow'}`}>
      {showTitle && (
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center">
            <span className="mr-2">🔗</span>
            Enlaces en Plataformas
          </h3>
          {compact && (
            <button
              onClick={() => setExpanded(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
      )}

      <div className="flex items-center space-x-3 mb-4">
        <button
          onClick={searchPlatformUrls}
          disabled={loading}
          className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
        >
          {loading ? (
            <>
              <span className="animate-spin">⏳</span>
              <span>Buscando...</span>
            </>
          ) : (
            <>
              <span>🚀</span>
              <span>Buscar Enlaces</span>
            </>
          )}
        </button>

        {urls && (
          <div className="text-sm text-gray-600">
            Código: <span className="font-mono font-semibold">{property.codigo_inmueble || property.id}</span>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <div className="flex items-center space-x-2 text-red-700">
            <span>❌</span>
            <span className="font-medium">Error:</span>
            <span>{error}</span>
          </div>
        </div>
      )}

      {urls && (
        <div className="space-y-4">
          {/* Resumen */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{urls.summary.found_count}</div>
                <div className="text-gray-600">Encontrados</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{urls.summary.success_rate}</div>
                <div className="text-gray-600">Éxito</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{urls.summary.total_platforms}</div>
                <div className="text-gray-600">Plataformas</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">{urls.summary.total_time_ms}ms</div>
                <div className="text-gray-600">Tiempo</div>
              </div>
            </div>
            {urls.summary.best_platform && (
              <div className="mt-3 text-center text-sm text-gray-600">
                🏆 Mejor resultado: <span className="font-semibold">
                  {PLATFORM_NAMES[urls.summary.best_platform]?.name || urls.summary.best_platform}
                </span>
              </div>
            )}
          </div>

          {/* Enlaces por plataforma */}
          <div className="grid gap-3">
            {Object.entries(PLATFORM_NAMES).map(([key, platform]) => {
              const url = urls.platform_urls[key];
              const hasUrl = url && url !== 'No encontrado' && url !== '';

              return (
                <div 
                  key={key}
                  className={`border rounded-lg p-3 transition-all ${
                    hasUrl 
                      ? 'border-green-200 bg-green-50 hover:bg-green-100' 
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full ${platform.color} flex items-center justify-center text-white text-sm font-bold`}>
                        {platform.icon}
                      </div>
                      <div>
                        <div className="font-medium text-gray-800">{platform.name}</div>
                        <div className={`text-xs ${hasUrl ? 'text-green-600' : 'text-gray-500'}`}>
                          {hasUrl ? '✅ Encontrado' : '❌ No encontrado'}
                        </div>
                      </div>
                    </div>

                    {hasUrl && (
                      <div className="flex items-center space-x-2">
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm flex items-center space-x-1"
                        >
                          <span>🔗</span>
                          <span>Ver</span>
                        </a>
                        <button
                          onClick={() => navigator.clipboard.writeText(url)}
                          className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm"
                          title="Copiar URL"
                        >
                          📋
                        </button>
                      </div>
                    )}
                  </div>

                  {hasUrl && (
                    <div className="mt-2 text-xs text-gray-600 font-mono truncate">
                      {url}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Timestamp */}
          <div className="text-xs text-gray-500 text-center">
            Última búsqueda: {new Date(urls.timestamp).toLocaleString('es-CO')}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformUrls;
